import { WarningOutlined } from '@ant-design/icons';
import { Alert, Empty, Modal, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { TableColumnsType } from 'antd';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../../api/errors';
import {
  ProjectTaskProgressSuspicion,
  listProjectTaskProgressLog,
  listSuspiciousProjectProgress,
  projectTaskProgressMethodNames,
  projectTaskProgressSuspicionLabels,
} from '../../api/projects';
import type {
  ProjectTaskItem,
  ProjectTaskProgressLogItem,
  ProjectTaskProgressMethodValue,
  ProjectTaskStatusValue,
} from '../../api/projects';
import { formatDateTime } from './projectDisplay';
import { taskStatusTag } from './taskDisplay';

/**
 * Các cờ dấu hiệu, theo đúng thứ tự hiển thị.
 *
 * Liệt kê tường minh thay vì suy ra từ đối tượng nhãn: thứ tự các khóa của một đối tượng không phải
 * thứ tự người đọc cần, và dấu hiệu đứng yên là dấu hiệu đáng chú ý nhất nên nó đứng đầu.
 */
const suspicionFlags = [
  ProjectTaskProgressSuspicion.Stalled,
  ProjectTaskProgressSuspicion.ContradictsRemainingEffort,
  ProjectTaskProgressSuspicion.SuddenJump,
];

/** Đổi tập cờ thành danh sách câu hỏi đọc được. */
function describeSignals(signals: number): readonly string[] {
  return suspicionFlags
    .filter((flag) => (signals & flag) !== 0)
    .map((flag) => projectTaskProgressSuspicionLabels[flag] ?? '');
}

/**
 * Hộp thoại lịch sử báo tiến độ.
 *
 * <b>Hai chế độ, cùng một bảng.</b> Mở từ một công việc thì hiện lịch sử của chính việc đó; mở từ
 * thanh công cụ thì hiện những lần báo có dấu hiệu của cả dự án. Cùng một cách đọc cho cả hai, vì
 * người dùng đang tìm cùng một thứ: <i>con số này đã đi qua những đâu</i>.
 *
 * <b>Không có nút sửa và không có nút xóa.</b> Bảng phía dưới chỉ ghi thêm — một lần báo nhầm và một
 * lần báo sửa lại là hai dòng. Cho sửa ở đây sẽ xóa mất đúng thứ mà cả màn hình này tồn tại để hiện.
 */
export function ProjectProgressLogModal({
  projectId,
  task,
  open,
  onClose,
}: {
  readonly projectId: string;

  /** Công việc cần xem lịch sử, hoặc rỗng để xem những lần báo đáng ngờ của cả dự án. */
  readonly task: ProjectTaskItem | null;

  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const [rows, setRows] = useState<readonly ProjectTaskProgressLogItem[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isProjectWide = task === null;

  useEffect(() => {
    if (!open) {
      return;
    }

    let isCurrent = true;

    async function load(taskId: string | null): Promise<void> {
      setErrorMessage(null);
      setRows(null);

      try {
        const loaded =
          taskId === null
            ? await listSuspiciousProjectProgress(projectId)
            : await listProjectTaskProgressLog(projectId, taskId);

        if (isCurrent) {
          setRows(loaded);
        }
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được lịch sử báo tiến độ.',
        );
      }
    }

    void load(task?.id ?? null);

    return () => {
      isCurrent = false;
    };
  }, [projectId, task, open]);

  const columns: TableColumnsType<ProjectTaskProgressLogItem> = [
    {
      title: 'Thời điểm',
      dataIndex: 'reportedAt',
      width: 160,
      render: (reportedAt: string) => formatDateTime(reportedAt),
    },
    ...(isProjectWide
      ? [
          {
            title: 'Công việc',
            dataIndex: 'taskName',
            render: (taskName: string, row: ProjectTaskProgressLogItem) => (
              <Space size={4} wrap>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {row.taskWbsCode}
                </Typography.Text>
                <span>{taskName}</span>
              </Space>
            ),
          },
        ]
      : []),
    {
      title: 'Tiến độ',
      dataIndex: 'newProgressPercent',
      width: 130,
      render: (percent: number, row) => (
        <Space size={4}>
          {row.previousProgressPercent === null ? null : (
            <Typography.Text type="secondary">
              {row.previousProgressPercent.toString()}% →
            </Typography.Text>
          )}
          <Typography.Text strong>{percent.toString()}%</Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Giờ còn lại',
      dataIndex: 'newRemainingEffortHours',
      width: 110,
      align: 'right',
      render: (hours: number | null) => hours ?? '—',
    },
    {
      title: 'Cách đo',
      dataIndex: 'progressMethodUsed',
      width: 170,
      render: (method: ProjectTaskProgressMethodValue) => projectTaskProgressMethodNames[method],
    },
    {
      title: 'Trạng thái lúc báo',
      dataIndex: 'statusAtReport',
      width: 150,
      render: (status: ProjectTaskStatusValue) => taskStatusTag(status, null),
    },
    {
      title: 'Dấu hiệu',
      dataIndex: 'suspicionSignals',
      width: 240,
      render: (signals: number) => {
        const questions = describeSignals(signals);

        if (questions.length === 0) {
          return <Typography.Text type="secondary">—</Typography.Text>;
        }

        return (
          <Space direction="vertical" size={2}>
            {questions.map((question) => (
              <Tooltip
                key={question}
                title="Dấu hiệu này không chặn gì cả. Nó ở đây để hỏi đúng câu vào đúng lúc, thay vì phát hiện khi đã trễ hạn."
              >
                <Tag color="warning" icon={<WarningOutlined />}>
                  {question}
                </Tag>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
  ];

  return (
    <Modal
      open={open}
      title={
        isProjectWide
          ? 'Những con số đáng ngờ của dự án'
          : `Lịch sử báo tiến độ — ${task.wbsCode} ${task.name}`
      }
      width={1080}
      footer={null}
      onCancel={onClose}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {errorMessage !== null ? <Alert type="error" showIcon message={errorMessage} /> : null}

        <Table<ProjectTaskProgressLogItem>
          rowKey="id"
          size="small"
          pagination={false}
          scroll={{ y: 420 }}
          columns={columns}
          dataSource={rows as ProjectTaskProgressLogItem[] | undefined}
          loading={rows === null && errorMessage === null}
          locale={{
            emptyText: (
              <Empty
                description={
                  isProjectWide
                    ? 'Không có con số nào đáng ngờ. Không có việc nào đứng yên, không có bước nhảy bất thường.'
                    : 'Công việc này chưa được báo tiến độ lần nào.'
                }
              />
            ),
          }}
        />

        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Bảng này chỉ ghi thêm nên không sửa và không xóa được: một lần báo nhầm và một lần báo sửa
          lại là hai dòng. Một việc báo 90% hôm nay trông không khác gì một việc báo 90% suốt ba
          tuần, và sự khác nhau ấy chỉ nằm ở đây.
        </Typography.Text>
      </Space>
    </Modal>
  );
}
